import React, { useState, useEffect, useCallback } from 'react';
import { 
    getRedeemSubscriptions, 
    getSubscriptionTiers, 
    createRedeemSubscription, 
    deleteRedeemSubscription,
    adminCreateDiscountCode,
    adminListDiscountCodes,
    adminDeleteDiscountCode,
    adminCreateAccessPass,
    adminListAccessPass,
    adminDeleteAccessPass
} from '../services/api';

const AdminPage = () => {
    const [redeemSubscriptions, setRedeemSubscriptions] = useState([]);
    const [subscriptionTiers, setSubscriptionTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('redeem'); // 'redeem' | 'discount' | 'access_pass'
    // Access Pass state
    const [accessPassCodes, setAccessPassCodes] = useState([]);
    const [newAccessPass, setNewAccessPass] = useState({
        durationDays: 30,
        allowedTierNames: 'miva_lite,miva_pro,miva_advanced',
        totalCount: 100,
        country: 'DEFAULT'
    });


    // Form state
    const [newOffer, setNewOffer] = useState({
        subscriptionName: '',
        subscriptionTierId: '',
        deposit: false,
        trialDurationDays: 15,
        redeemCount: 10,
        country: 'TW'
    });

    // Discount code state
    const [discounts, setDiscounts] = useState([]);
    const [newDiscount, setNewDiscount] = useState({
        discountFactor: 0.8,
        totalCount: 100,
        activationWindowDays: 30
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [redeemRes, tiersRes, discountRes, accessPassRes] = await Promise.all([
                getRedeemSubscriptions(),
                getSubscriptionTiers(),
                adminListDiscountCodes(),
                adminListAccessPass()
            ]);
            setRedeemSubscriptions(redeemRes.data.data?.items || []);
            setSubscriptionTiers(tiersRes.data.data || []);
            setDiscounts(discountRes.data.data?.items || []);
            setAccessPassCodes(accessPassRes.data.data || []);
            // Set default tier selection
            if (tiersRes.data.data?.length > 0) {
                setNewOffer(prev => ({ ...prev, subscriptionTierId: tiersRes.data.data[0].id }));
            }
        } catch (err) {
            setError('無法獲取資料，請稍後再試。');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);
    const handleAccessPassCreate = async (e) => {
        e.preventDefault();
        if (!newAccessPass.durationDays || !newAccessPass.allowedTierNames) {
            alert('請完整填寫通行證欄位');
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = {
                durationDays: parseInt(String(newAccessPass.durationDays), 10) || 0,
                allowedTierNames: newAccessPass.allowedTierNames.split(',').map(s => s.trim()).filter(Boolean),
                totalCount: parseInt(String(newAccessPass.totalCount), 10) || 0,
                country: newAccessPass.country || null
            };
            const resp = await adminCreateAccessPass(payload);
            const createdCode = resp?.data?.data?.redeemCode;
            alert(`Access Pass 建立成功！代碼：${createdCode || '(請至列表查看)'}`);
            setNewAccessPass({ durationDays: 30, allowedTierNames: 'miva_lite,miva_pro,miva_advanced', totalCount: 100, country: 'DEFAULT' });
            await fetchData();
        } catch (err) {
            alert(`建立失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteAccessPass = async (id) => {
        if (!window.confirm('確定要刪除此 Access Pass 代碼嗎？此動作無法復原。')) return;
        try {
            await adminDeleteAccessPass(id);
            alert('Access Pass 已刪除。');
            setAccessPassCodes(prev => prev.filter(item => item.id !== id));
        } catch (err) {
            alert(`刪除失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        }
    };


    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewOffer(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newOffer.subscriptionName || !newOffer.subscriptionTierId) {
            alert('請填寫方案名稱並選擇方案等級。');
            return;
        }
        setIsSubmitting(true);
        try {
            await createRedeemSubscription(newOffer);
            alert('兌換碼方案已成功建立！');
            setNewOffer({ // Reset form
                subscriptionName: '',
                subscriptionTierId: subscriptionTiers[0]?.id || '',
                deposit: false,
                trialDurationDays: 15,
                redeemCount: 10,
                country: 'TW'
            });
            await fetchData(); // Refresh list
        } catch (err) {
            alert(`建立失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDiscountInputChange = (e) => {
        const { name, value } = e.target;
        setNewDiscount(prev => ({ ...prev, [name]: name === 'discountFactor' ? parseFloat(value) : parseInt(value || '0', 10) || value }));
    };

    const handleCreateDiscount = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await adminCreateDiscountCode(newDiscount);
            alert('折扣碼建立成功！');
            setNewDiscount({ discountFactor: 0.8, totalCount: 100, activationWindowDays: 30 });
            await fetchData();
        } catch (err) {
            alert(`建立失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteDiscount = async (id) => {
        if (!window.confirm('確定要刪除此折扣碼嗎？此動作無法復原。')) return;
        try {
            await adminDeleteDiscountCode(id);
            alert('折扣碼已刪除。');
            setDiscounts(prev => prev.filter(item => item.id !== id));
        } catch (err) {
            alert(`刪除失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('您確定要刪除這個兌換碼方案嗎？此操作無法復原。')) return;
        try {
            await deleteRedeemSubscription(id);
            alert('方案已成功刪除。');
            setRedeemSubscriptions(prev => prev.filter(item => item.redeemSubscriptionId !== id));
        } catch (err) {
            alert(`刪除失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        }
    };

    const handleCopy = (text) => {
        if (navigator.clipboard && window.isSecureContext) {
            // Use the modern clipboard API in secure contexts
            navigator.clipboard.writeText(text).then(() => {
                alert('兌換碼已複製到剪貼簿！');
            }).catch(err => {
                console.error('無法使用 Clipboard API 複製:', err);
                alert('複製失敗。');
            });
        } else {
            // Fallback for non-secure contexts or older browsers
            const textArea = document.createElement("textarea");
            textArea.value = text;
            
            // Make the textarea out of sight
            textArea.style.position = "fixed";
            textArea.style.top = "-9999px";
            textArea.style.left = "-9999px";
            
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
                const successful = document.execCommand('copy');
                if (successful) {
                    alert('兌換碼已複製到剪貼簿！');
                } else {
                    alert('複製失敗，您的瀏覽器可能不支援此功能。');
                }
            } catch (err) {
                console.error('無法使用 execCommand 複製:', err);
                alert('複製失敗，您的瀏覽器可能不支援此功能。');
            }
            
            document.body.removeChild(textArea);
        }
    };

    return (
        <div className="page-container">
            <div className="page-header">
                <h2>管理兌換碼方案</h2>
                <button onClick={fetchData} disabled={loading} className="refresh-button">
                    {loading ? '刷新中...' : '重新整理'}
                </button>
            </div>

            {/* Tabs */}
            <div className="tab-header" style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                    onClick={() => setActiveTab('redeem')}
                    className={`action-button ${activeTab === 'redeem' ? 'default' : ''}`}
                >
                    兌換碼方案
                </button>
                <button
                    onClick={() => setActiveTab('discount')}
                    className={`action-button ${activeTab === 'discount' ? 'default' : ''}`}
                >
                    折扣碼管理
                </button>
                <button
                    onClick={() => setActiveTab('access_pass')}
                    className={`action-button ${activeTab === 'access_pass' ? 'default' : ''}`}
                >
                    Access Pass 管理
                </button>
            </div>

            {error && <p className="error-message">{error}</p>}
            
            {activeTab === 'redeem' && (
                <>
                    {/* Create New Offer Form */}
                    <div className="admin-section">
                        <h3>建立新的兌換碼方案</h3>
                        <form onSubmit={handleCreate} className="admin-form">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>方案名稱</label>
                                    <input type="text" name="subscriptionName" value={newOffer.subscriptionName} onChange={handleInputChange} required />
                                </div>
                                <div className="form-group">
                                    <label>方案等級 (Tier)</label>
                                    <select name="subscriptionTierId" value={newOffer.subscriptionTierId} onChange={handleInputChange} required>
                                        {subscriptionTiers.map(tier => (
                                            <option key={tier.id} value={tier.id}>{tier.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>試用天數</label>
                                    <input type="number" name="trialDurationDays" value={newOffer.trialDurationDays} onChange={handleInputChange} min="0" required />
                                </div>
                                <div className="form-group">
                                    <label>可兌換數量</label>
                                    <input type="number" name="redeemCount" value={newOffer.redeemCount} onChange={handleInputChange} min="1" required />
                                </div>
                                <div className="form-group">
                                    <label>國家代碼</label>
                                    <input type="text" name="country" value={newOffer.country} onChange={handleInputChange} required />
                                </div>
                                <div className="form-group-checkbox">
                                    <input type="checkbox" id="deposit" name="deposit" checked={newOffer.deposit} onChange={handleInputChange} />
                                    <label htmlFor="deposit">是否支援儲值 (Deposit)</label>
                                </div>
                            </div>
                            <button type="submit" disabled={isSubmitting} className="action-button default">
                                {isSubmitting ? '建立中...' : '建立方案'}
                            </button>
                        </form>
                    </div>

                    {/* Existing Offers Table */}
                    <div className="admin-section">
                        <h3>現有的兌換碼方案</h3>
                        <div className="table-container">
                            <table className="history-table">
                                <thead>
                                    <tr>
                                        <th>方案名稱</th>
                                        <th>兌換碼</th>
                                        <th>試用天數</th>
                                        <th>已兌換 / 總數</th>
                                        <th>建立時間</th>
                                        <th>操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!loading && redeemSubscriptions.length > 0 ? (
                                        redeemSubscriptions.map(item => (
                                            <tr key={item.redeemSubscriptionId}>
                                                <td>{item.redeemSubscriptionName}</td>
                                                <td>
                                                    <code>{item.redeemCode}</code>
                                                    <button 
                                                        onClick={() => handleCopy(item.redeemCode)}
                                                        className="action-button copy-button"
                                                        title="複製兌換碼"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                        </svg>
                                                    </button>
                                                </td>
                                                <td>{item.trialDurationDays} 天</td>
                                                <td>{item.activatedCount} / {item.totalCount}</td>
                                                <td>{new Date(item.createdAt).toLocaleString()}</td>
                                                <td>
                                                    <button onClick={() => handleDelete(item.redeemSubscriptionId)} className="action-button destructive">刪除</button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="6">{loading ? '載入中...' : '沒有任何兌換碼方案。'}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'discount' && (
                <div className="admin-section">
                    <h3>折扣碼管理</h3>
                    <form onSubmit={handleCreateDiscount} className="admin-form">
                        <div className="form-grid">
                            <div className="form-group">
                                <label>折扣係數（0.1 ~ 1.0）</label>
                                <input type="number" min="0.1" max="1" step="0.05" name="discountFactor" value={newDiscount.discountFactor} onChange={handleDiscountInputChange} required />
                            </div>
                            <div className="form-group">
                                <label>總數</label>
                                <input type="number" min="1" name="totalCount" value={newDiscount.totalCount} onChange={handleDiscountInputChange} required />
                            </div>
                            <div className="form-group">
                                <label>啟用後有效天數</label>
                                <input type="number" min="1" name="activationWindowDays" value={newDiscount.activationWindowDays} onChange={handleDiscountInputChange} required />
                            </div>
                        </div>
                        <button type="submit" disabled={isSubmitting} className="action-button default">
                            {isSubmitting ? '建立中...' : '建立折扣碼'}
                        </button>
                    </form>

                    <div className="table-container" style={{ marginTop: '1rem' }}>
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>折扣碼（系統產生）</th>
                                    <th>折扣係數</th>
                                    <th>已啟用 / 總數</th>
                                    <th>有效天數</th>
                                    <th>建立時間</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {discounts.length > 0 ? discounts.map(code => (
                                    <tr key={code.id}>
                                        <td>
                                            <code>{code.redeem_code}</code>
                                            <button 
                                                onClick={() => handleCopy(code.redeem_code)}
                                                className="action-button copy-button"
                                                title="複製折扣碼"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                </svg>
                                            </button>
                                        </td>
                                        <td>{code.discount_factor}</td>
                                        <td>{code.activated_count} / {code.total_count}</td>
                                        <td>{code.activation_window_days} 天</td>
                                        <td>{new Date(code.created_at).toLocaleString()}</td>
                                        <td>
                                            <button onClick={() => handleDeleteDiscount(code.id)} className="action-button destructive">刪除</button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="6">尚無折扣碼</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'access_pass' && (
                <div className="admin-section">
                    <h3>Access Pass 管理</h3>
                    <form onSubmit={handleAccessPassCreate} className="admin-form">
                        <div className="form-grid">
                            <div className="form-group">
                                <label>有效天數</label>
                                <input type="number" min="1" value={newAccessPass.durationDays} onChange={(e) => setNewAccessPass(prev => ({ ...prev, durationDays: e.target.value }))} required />
                            </div>
                            <div className="form-group">
                                <label>允許的 Tiers（以逗號分隔）</label>
                                <input type="text" value={newAccessPass.allowedTierNames} onChange={(e) => setNewAccessPass(prev => ({ ...prev, allowedTierNames: e.target.value }))} required />
                            </div>
                            <div className="form-group">
                                <label>總名額</label>
                                <input type="number" min="1" value={newAccessPass.totalCount} onChange={(e) => setNewAccessPass(prev => ({ ...prev, totalCount: e.target.value }))} required />
                            </div>
                            <div className="form-group">
                                <label>國家（選填，預設 DEFAULT）</label>
                                <input type="text" value={newAccessPass.country} onChange={(e) => setNewAccessPass(prev => ({ ...prev, country: e.target.value }))} />
                            </div>
                        </div>
                        <button type="submit" disabled={isSubmitting} className="action-button default">
                            {isSubmitting ? '建立中...' : '建立 Access Pass'}
                        </button>
                    </form>

                    <div className="table-container" style={{ marginTop: '1rem' }}>
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>代碼</th>
                                    <th>有效天數</th>
                                    <th>允許 Tiers</th>
                                    <th>已啟用 / 總數</th>
                                    <th>國家</th>
                                    <th>建立時間</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accessPassCodes.length > 0 ? accessPassCodes.map(code => (
                                    <tr key={code.id}>
                                        <td>
                                            <code>{code.redeem_code}</code>
                                            <button 
                                                onClick={() => handleCopy(code.redeem_code)}
                                                className="action-button copy-button"
                                                title="複製通行證代碼"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                </svg>
                                            </button>
                                        </td>
                                        <td>{code.duration_days} 天</td>
                                        <td>{Array.isArray(code.allowed_tier_names) ? code.allowed_tier_names.join(', ') : ''}</td>
                                        <td>{code.activated_count} / {code.total_count}</td>
                                        <td>{code.country || '-'}</td>
                                        <td>{new Date(code.created_at).toLocaleString()}</td>
                                        <td>
                                            <button onClick={() => handleDeleteAccessPass(code.id)} className="action-button destructive">刪除</button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="7">尚無 Access Pass</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPage;